import { cn } from '@/lib/cn';

/**
 * Compact gradient avatar for an agent — used in the decisions ticker,
 * agent picker, and timeline events. Looks up gradient + initials from a
 * normalized agent name; falls back to a neutral gradient + first two
 * letters for unknown agents.
 */
const GRADIENTS: Record<string, { gradient: string; initials: string }> = {
  RESEARCH:          { gradient: 'bg-av-research',    initials: 'RE' },
  PORTFOLIO_MANAGER: { gradient: 'bg-av-pm-value',    initials: 'PM' },
  PM_VALUE:          { gradient: 'bg-av-pm-value',    initials: 'PV' },
  PM_MOMENTUM:       { gradient: 'bg-av-pm-momentum', initials: 'PM' },
  RISK_MANAGER:      { gradient: 'bg-av-risk',        initials: 'RI' },
  RISK:              { gradient: 'bg-av-risk',        initials: 'RI' },
  DISCOVERY:         { gradient: 'bg-av-discovery',   initials: 'DI' },
  WEB_RESEARCH:      { gradient: 'bg-av-web',         initials: 'WR' },
  MARKET_PULSE:      { gradient: 'bg-av-pulse',       initials: 'MP' },
};

interface Props {
  agent: string | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE: Record<NonNullable<Props['size']>, string> = {
  sm: 'w-6 h-6 text-[9px] rounded-md',
  md: 'w-8 h-8 text-[11px] rounded-lg',
  lg: 'w-11 h-11 text-sm rounded-xl',
};

export function AgentAvatar({ agent, size = 'md', className }: Props) {
  const key = (agent ?? '').toUpperCase().replace(/-/g, '_');
  const meta = GRADIENTS[key];
  const initials = meta?.initials ?? (agent ? agent.slice(0, 2).toUpperCase() : '??');
  const gradient = meta?.gradient ?? 'bg-surface-3';

  return (
    <div
      title={agent ?? 'unknown'}
      className={cn(
        'grid place-items-center font-bold text-white flex-none tracking-tight',
        SIZE[size],
        gradient,
        className,
      )}
    >
      {initials}
    </div>
  );
}

/** Lowercase, hyphenated display name: PM_VALUE → "pm-value". */
export function displayAgent(agent: string | null | undefined): string {
  if (!agent) return 'unknown';
  return agent.toLowerCase().replace(/_/g, '-');
}
